/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useQuery } from 'react-query'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { merge } from 'lodash'
import classNames from 'classnames'
import UsDetailPage from './USDetailPage'
import { HEADER_OFFSET_TAILWIND_CLASS } from './utils/top-headers-offset'
import { USL2MobileBottomStickyLazy } from './components/mobile-bottom-sticky/mobile-bottom-sticky-lazy'
import { TUSStocksL2PageProps } from './get-ssr-propsV1'
import { LoggedInSidebarUSL2Lazy } from './components/logged-in-sidebar/logged-in-sidebar-lazy'
import USL2Breadcrumbs from './components/breadcrumbs'
import { TAssetDetailsData } from '@/components/Stocks/Widgets/types'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import SomethingWentWrong from '@/components/Common/SomethingWentWrong'
import { TCurrency } from '@/components/catalogue/types'
import { IHeaders } from '@/components/Stocks/Pages/StockDetail'
import { usStockCatalogueUrl } from '@/root/api/apiConstants'
import { addQueryParamsToApiV2 } from '@/root/helpers'
import { StrapiV4GreenStickyCTA } from '@/stories/strapi-4/widget-components/green-sticky-cta/green-sticky-cta'
import getChildPageSEOData from '@/root/utils/us-stocks/get-child-page-seo-data'
import SEOHeader, { SEOProps } from '@/components/Common/SEOHeader'
import ScrollAwareTopNav from '@/components/ScrollAwareV2/ScrollAwareV2'
import { getUSL2TopNav } from '@/root/utils/us-stocks/getUSL2TopNav'

export default function USStocksL2PageV2({
  pageApiResponse,
  strapiApiResponse,
  isMobile,
  usStocksRelatedBlogs,
  ticker,
  token,
  resolvedUrl,
  member_id,
  headers,
}: TUSStocksL2PageProps & {
  headers: IHeaders
  host: string
}) {
  const [currency, setCurrency] = useState<TCurrency>('USD')

  const onCurrencyToggle = () => {
    setCurrency(currency === 'USD' ? 'INR' : 'USD')
  }

  /* ROUTER */
  const router = useRouter()
  const { query = {} } = router
  const { pn1: stockSlug = '' } = query

  const { data, isLoading, isError } = useQuery<TAssetDetailsData>(
    ['usstocksDetails', stockSlug],
    () =>
      fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: addQueryParamsToApiV2(usStockCatalogueUrl(), {
          response_format: 'web',
          'catalog-required': 'true',
          'graph-optional': 'true',
          id: ticker,
          currency,
          ...(member_id && { member_id: member_id.toString() }),
        }),
      }),
    {
      retry: 0,
      staleTime: 3000,
      enabled: !!ticker,
      initialData: pageApiResponse ?? {},
    },
  )

  const { data: stockApiData, isLoading: stockApiLoading } =
    useQuery<TAssetDetailsData>(
      ['catalogue', stockSlug, currency],
      () =>
        fetchRequest({
          config: {
            headers,
          },
          timeout: 0,
          url: addQueryParamsToApiV2(usStockCatalogueUrl(), {
            response_format: 'web',
            'catalog-required': 'true',
            id: ticker,
            currency,
            ...(member_id && { member_id: member_id.toString() }),
          }),
        }),
      {
        retry: 0,
        staleTime: 3000,
        enabled: !!ticker,
      },
    )

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <SpinnerLoader />
      </div>
    )
  }

  if (isError) {
    return (
      <section className="relative mb-36 mt-4 bg-white">
        <div className="lg:min-w-4/5 lg:flex lg:max-w-full lg:flex-col lg:items-center lg:justify-center">
          <SomethingWentWrong />
        </div>
      </section>
    )
  }

  const combineData = merge(data, stockApiData)

  const navItems = getUSL2TopNav({
    stock_details: combineData?.catalog?.stock_details,
    entity_stats: combineData?.catalog?.entity_stats,
    usRelatedBlogs: usStocksRelatedBlogs,
  })

  const basicData = combineData?.catalog?.entity_basic
  const statsData = combineData?.catalog?.entity_stats

  const stockSummary = {
    company_name: basicData?.name ?? '',
    delisted: !basicData?.is_active,
    percentage_change: statsData?.day_change_percentage,
    ticker,
    logo: basicData?.logo ?? '',
    live_price: statsData?.live_price?.toString() ?? '',
    exchange: basicData?.exchange ?? '',
    day_change: statsData?.day_change ?? 0,
  }

  const meta_info = {
    currency_symbol: combineData?.currency_symbol ?? '$',
  }

  const seoMakerOptions = {
    stockData: {
      overview: stockSummary,
      currency_INR: statsData?.live_price,
    },
    path: resolvedUrl,
  }

  let seoData: SEOProps['data'] = getChildPageSEOData(seoMakerOptions)

  if (strapiApiResponse?.data.attributes.seo) {
    const finalSchemas = [
      ...(seoData.schemas ?? []),
      ...(strapiApiResponse.data.attributes.seo.schemas ?? []),
    ]
    seoData = {
      ...seoData,
      ...strapiApiResponse.data.attributes.seo,
      schemas: finalSchemas,
    }
  }

  return (
    <div>
      <SEOHeader data={seoData} />
      <ScrollAwareTopNav navItems={navItems} isMobile={!!isMobile} />
      <div className="herolike-wrapper flex flex-col items-center">
        <div className="mt-4 w-full">
          <USL2Breadcrumbs
            companyName={basicData?.name ?? ''}
            ticker={ticker ?? ''}
            slug={stockSlug as string}
          />
        </div>
        <div className="mt-6 flex w-full items-start justify-between xl:mt-8">
          <UsDetailPage
            containerClassName={classNames({
              'max-w-[840px]': !!token,
            })}
            {...combineData}
            stockApiLoading={stockApiLoading}
            isMobile={isMobile}
            strapiApiResponse={strapiApiResponse}
            currency={currency}
            onCurrencyToggle={onCurrencyToggle}
            usStocksRelatedBlogs={usStocksRelatedBlogs}
            ticker={ticker}
            token={token as string | null}
            meta_info={meta_info}
          />
          {token && !isMobile && (
            <div
              className={classNames(
                'hidden w-1/4 max-w-[352px] self-start xl:block',
                HEADER_OFFSET_TAILWIND_CLASS,
              )}
            >
              <LoggedInSidebarUSL2Lazy {...stockSummary} />
            </div>
          )}
        </div>
      </div>

      {token && isMobile && <USL2MobileBottomStickyLazy {...stockSummary} />}
      {!token && ticker && (
        <StrapiV4GreenStickyCTA text={`Invest in ${ticker}`} />
      )}
    </div>
  )
}
